import { type CSSProperties, type MouseEvent } from 'react';
import { PopupButtonPrimitive } from '@figma/fpl-components';
import { Icon24ChevronDown } from '@figma/fpl-icons';
import clsx from 'clsx';
import * as stylex from '@stylexjs/stylex';

import styles from './FplMenuCaretButton.module.css';

export interface FplMenuCaretButtonProps {
  'aria-label': string;
  'aria-expanded'?: boolean;
  disabled?: boolean;
  /** Match the adjacent control height. */
  height?: number;
  onClick: (event: MouseEvent) => void;
}

/** Shared hit-target width for painter and video — Figma `57501:27074`. */
export const FPL_MENU_CARET_WIDTH = 12;

/**
 * Shared painter + video menu caret. Optical centering lives in
 * `FplMenuCaretButton.module.css` (same Trigger formula for every caller).
 */
export function FplMenuCaretButton({
  'aria-label': ariaLabel,
  'aria-expanded': ariaExpanded,
  disabled = false,
  height = 24,
  onClick,
}: FplMenuCaretButtonProps) {
  const caretWidthStyle = {
    '--fpl-menu-caret-width': `${FPL_MENU_CARET_WIDTH}px`,
    '--icon-button-width': `${FPL_MENU_CARET_WIDTH}px`,
  } as CSSProperties;

  return (
    <span {...stylex.props(stylesRoot.root)}>
      <PopupButtonPrimitive
        data-fpl-component=""
        aria-label={ariaLabel}
        aria-expanded={ariaExpanded ?? false}
        aria-haspopup="menu"
        data-tooltip={ariaLabel}
        data-tooltip-type="text"
        disabled={disabled}
        onClick={onClick}
        className={clsx(styles.caretButton, height >= 32 && styles.caretButtonLarge)}
        style={caretWidthStyle}
      >
        <span className={styles.caretIcon} aria-hidden>
          <Icon24ChevronDown />
        </span>
      </PopupButtonPrimitive>
    </span>
  );
}

const stylesRoot = stylex.create({
  root: {
    display: 'inline-flex',
    flexShrink: 0,
  },
});
